import { useSyncExternalStore, type MouseEvent } from 'react';
import { useAuth } from 'context/AuthContext';
import { hidesPurchasePrices } from 'utils/helpers/access/hasUserAccess';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'context/SettingsContext';
import { formatPrice } from 'utils/helpers/functions/formatPrice';
import {
    withLocalCurrency,
    withPrimaryCurrency,
} from 'utils/helpers/currency/currencyHelpers';
import EToolTip from 'components/others/EToolTip';

const STORAGE_KEY = 'priceAndPriceSale.saleOnTop';

type Listener = () => void;

const listeners = new Set<Listener>();

const readSaleOnTop = (): boolean => {
    try {
        return window.localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
        return false;
    }
};

let saleOnTop = typeof window !== 'undefined' ? readSaleOnTop() : false;

const emit = () => {
    listeners.forEach((listener) => listener());
};

const subscribeSaleOnTop = (listener: Listener): (() => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

const getSaleOnTop = (): boolean => saleOnTop;

const toggleSaleOnTop = (): void => {
    saleOnTop = !saleOnTop;
    try {
        window.localStorage.setItem(STORAGE_KEY, saleOnTop ? '1' : '0');
    } catch {
        // ignore quota / private mode
    }
    emit();
};

interface PriceAndPriceSaleProps {
    price?: number | null;
    priceSale?: number | null;
}

const stopEvent = (e: MouseEvent) => {
    e.stopPropagation();
};

const PriceAndPriceSale = ({ price, priceSale }: PriceAndPriceSaleProps) => {
    const { t } = useTranslation(['common']);
    const { auth } = useAuth();
    const hidePurchase = hidesPurchasePrices(auth?.user);
    const { localSettings } = useSettings();
    const isSaleOnTop = useSyncExternalStore(subscribeSaleOnTop, getSaleOnTop, () => false);
    const rate = Number(localSettings?.RATE?.current ?? 1) || 1;
    const isUahMode = rate === 1;

    const purchaseValue = Number(price) || 0;
    const saleValue = Number(priceSale) || 0;

    const formatMain = (value: number) => withPrimaryCurrency(formatPrice(value), rate);
    const formatTooltipAmount = (value: number) => {
        const main = formatMain(value);
        if (isUahMode) return main;
        return `${main} (${withLocalCurrency(formatPrice(value * rate))})`;
    };

    const purchaseLabel = t('priceAndPriceSale.purchase');
    const saleLabel = t('priceAndPriceSale.sale');

    const topValue = isSaleOnTop ? saleValue : purchaseValue;
    const bottomValue = isSaleOnTop ? purchaseValue : saleValue;
    const topLabel = isSaleOnTop ? saleLabel : purchaseLabel;
    const bottomLabel = isSaleOnTop ? purchaseLabel : saleLabel;

    if (hidePurchase) {
        return (
            <EToolTip title={saleLabel}>
                <h6 className="mb-0 text-primary">{formatMain(saleValue)}</h6>
            </EToolTip>
        );
    }

    const handleClick = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        toggleSaleOnTop();
    };

    return (
        <EToolTip
            title={<div>{topLabel} / {bottomLabel}</div>}>
            <div
                className="cursor-pointer text-truncate"
                onClick={handleClick}
                onMouseDown={stopEvent}
            >
                <h6 className={`mb-1${isSaleOnTop ? ' text-primary' : ''}`}>{formatMain(topValue)}</h6>
                <p
                    className={`f-12 mb-0 text-truncate${isSaleOnTop ? '' : ' text-primary'}`}
                >
                    {formatMain(bottomValue)}
                </p>
            </div>
        </EToolTip>
    );
};

export default PriceAndPriceSale;
